import React, { useState, useEffect, Children, cloneElement, isValidElement } from 'react';
import ResponsiveImage from '../../../../components/ResponsiveImage/ResponsiveImage';
import ImageCarousel from '../../../../components/ImageCarousel/ImageCarousel';
import { FormattedMessage } from '../../../../util/reactIntl';
import Modal from '../Utils/Modal/Modal';
import Gallery from 'react-photo-gallery';
import css from './ImageGallery.module.css';
import { filterListingImages, getHeroImageUrl, getImageVariant } from './listingImages.util';

const galleryIcon =
  'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082501/socialbnb/assets/icons/4Dots.png';

const ImageGallery = props => {
  const { listing, children, onModalStateChange } = props;

  const [showModal, setShowModal] = useState(false);
  const [showSlider, setShowSlider] = useState(false);
  const [gallerySelectedId, setGallerySelectedId] = useState(0);
  const [filteredImages, setFilteredImages] = useState([]);

  useEffect(() => {
    if (listing) {
      setFilteredImages(filterListingImages(listing));
    }
  }, [listing]);

  useEffect(() => {
    const isAnyModalOpen = showModal || showSlider;

    if (onModalStateChange) {
      onModalStateChange(isAnyModalOpen);
    }

    window.dispatchEvent(
      new CustomEvent('modalStateChange', {
        detail: { isOpen: isAnyModalOpen },
      })
    );

    if (isAnyModalOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.setAttribute('data-scroll-y', scrollY.toString());
    } else {
      const scrollY = document.body.getAttribute('data-scroll-y');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY, 10));
        document.body.removeAttribute('data-scroll-y');
      }
    }

    return () => {
      if (isAnyModalOpen) {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        const scrollY = document.body.getAttribute('data-scroll-y');
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY, 10));
          document.body.removeAttribute('data-scroll-y');
        }
      }
    };
  }, [showModal, showSlider, onModalStateChange]);

  if (!listing) {
    return null;
  }

  const amount = filteredImages.length;
  if (amount === 0) {
    return null;
  }

  const heroImageUrl = getHeroImageUrl(filteredImages);
  const thumbImages = filteredImages.slice(1, 6);
  const remainingPhotoCount = amount > 6 ? amount - 6 : 0;

  const mappedArray = filteredImages
    .filter(image => image?.attributes?.variants)
    .map((image, index) => {
      const { variants } = image.attributes;
      const variant = 'scaled-xlarge' in variants ? 'scaled-xlarge' : 'landscape-crop4x';
      return {
        id: index + 1,
        src: variants[variant]?.url,
        width: 400,
        height: 300,
        name: variants[variant]?.name,
        className: variant === 'scaled-xlarge' ? css.containImage : css.coverImage,
      };
    });

  const openGallery = () => setShowModal(true);
  const closeGallery = () => setShowModal(false);

  const openSlider = (index = 0) => {
    setShowSlider(true);
    setGallerySelectedId(index);
  };

  const openSliderFromGrid = e => {
    const targetIndex = Array.from(e.target.parentNode.children).indexOf(e.target);
    openSlider(targetIndex >= 0 ? targetIndex : 0);
  };

  const closeSlider = () => {
    setShowSlider(false);
    setShowModal(false);
  };

  const heroStyle = heroImageUrl
    ? { backgroundImage: `url(${heroImageUrl})` }
    : undefined;

  const handleHeroClick = () => {
    openGallery();
  };

  return (
    <div className={css.galleryWrapper} data-elbaction="click:click viewimages">
      <div className={css.hero} style={heroStyle} onClick={handleHeroClick}>
        <div className={css.heroGradient} aria-hidden="true" />
        <div className={css.heroOverlay}>
          {Children.map(children, child =>
            isValidElement(child) ? cloneElement(child, { photoCount: amount }) : child
          )}
        </div>
        <button
          type="button"
          className={css.viewAllBtn}
          onClick={event => {
            event.stopPropagation();
            openGallery();
          }}
        >
          <FormattedMessage id="ListingHeader.allPhotos" values={{ count: amount }} />
          <img className={css.viewAllIcon} src={galleryIcon} alt="" />
        </button>
      </div>

      {thumbImages.length > 0 && (
        <div className={css.thumbRail}>
          {thumbImages.map((image, index) => {
            const isLast = index === thumbImages.length - 1;
            const showMoreOverlay = isLast && remainingPhotoCount > 0;

            return (
              <button
                key={image?.id?.uuid || index}
                type="button"
                className={css.thumbCell}
                onClick={() => (showMoreOverlay ? openGallery() : openSlider(index + 1))}
                aria-label={
                  showMoreOverlay
                    ? `View all ${amount} photos`
                    : `Photo ${index + 2}`
                }
              >
                <ResponsiveImage
                  rootClassName={css.thumbImage}
                  alt=""
                  image={image}
                  loading="lazy"
                  variants={[getImageVariant(image)]}
                />
                {showMoreOverlay && (
                  <span className={css.thumbMoreOverlay}>+{remainingPhotoCount}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={closeGallery}
          classNameModal="galleryModal"
          classNameButton="buttonGallery"
          classNameBtnImage="btnImage"
        >
          <Gallery onClick={openSliderFromGrid} columns={3} photos={mappedArray} margin={5} />
        </Modal>
      )}

      {showSlider && (
        <Modal
          isOpen={showSlider}
          onClose={closeSlider}
          classNameModal="sliderModal"
          classNameButton="buttonSlider"
          classNameBtnImage="btnImage"
        >
          <ImageCarousel selectedImg={gallerySelectedId} images={filteredImages} />
        </Modal>
      )}
    </div>
  );
};

export default ImageGallery;
